import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../config/supabaseConfig.ts'

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Mapea el usuario de Supabase a un objeto compatible con los componentes existentes
const mapUser = (user) => {
  console.log('[AuthContext] user_metadata:', user.user_metadata);
  return {
    ...user,
    full_name: user.user_metadata?.full_name || user.email,
    role: user.user_metadata?.role || null,
  };
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Flujo de login en dos pasos:
   * 1. Intentar signInWithPassword directo (contraseña ya migrada)
   * 2. Si falla por credenciales inválidas → llamar edge function login-proxy
   *    que verifica la contraseña bcrypt original, actualiza el hash en auth.users
   *    y devuelve un token de sesión válido
   */
  const login = async (email, password) => {
    // Paso 1: intentar auth directo
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (!error) {
      return data.user;
    }

    // Solo intentar fallback si el error es de credenciales inválidas
    const isInvalidCredentials =
      error.message?.toLowerCase().includes('invalid login credentials') ||
      error.code === 'invalid_credentials' ||
      error.status === 400;

    if (!isInvalidCredentials) {
      throw error;
    }

    // Paso 2: fallback a edge function (migración de contraseña bcrypt)
    const { data: edgeData, error: edgeError } = await supabase.functions.invoke('login-proxy', {
      body: { email, password, role: 'admin' },
    });

    if (edgeError || !edgeData?.access_token) {
      throw new Error('Credenciales inválidas');
    }

    // Establecer sesión con el token devuelto por la edge function
    const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
      access_token: edgeData.access_token,
      refresh_token: edgeData.refresh_token,
    });

    if (sessionError) throw sessionError;
    return sessionData.user;
  };

  // Logout con Supabase Auth
  const logout = async () => {
    await supabase.auth.signOut();
  };

  // Inicializar sesión y suscribirse a cambios de auth
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setCurrentUser(session ? mapUser(session.user) : null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setCurrentUser(session ? mapUser(session.user) : null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Context value
  const value = {
    currentUser,
    login,
    logout,
    isAuthenticated: !!currentUser,
    role: currentUser?.role || null,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
